import { useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CalendarIcon,
  CurrencyRupeeIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  CardHeader,
  Badge,
  getStatusBadgeVariant,
  Modal,
  ModalFooter,
  ContentLoader,
  Select,
  EntityLink,
  InsurerLogo,
  BackButton,
  PolicyNumber,
  policyNumberLabel,
} from '@/components/common';
import { useRenewalQueueItem, useUpdateRenewalQueueItemStatus } from '@/hooks/useRenewals';
import { useAuthStore } from '@/store/auth';
import { useCan } from '@/hooks/useEmployeeRights';
import { formatDate, formatCurrency } from '@/utils';
import { renewalStatusOptions, renewalWorkflowStatusLabel } from './renewalStatusOptions';
import { RenewalPaymentLinkCard } from './RenewalPaymentLinkCard';
import type { RenewalQueuePolicyRef } from '@/types/renewalQueue';
import type { RenewalStatus } from '@/types';

// B9: the detail page now reads the policy-based queue
// (`GET /renewals/queue/<id>/`), where `<id>` may be a policy id or a legacy
// Renewal id -- the backend resolves either within scope, so this page never
// needs to know which kind it was handed. Status statusOptions excludes the
// queue-only "none" filter sentinel: that value means "no workflow row",
// never something a person sets by hand.
const statusOptions = renewalStatusOptions.filter((o) => o.value !== '' && o.value !== 'none');

export function RenewalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const isCustomer = user?.role === 'customer';
  // B4: Update Status is renewals Edit; the two links open screens that need
  // policies Create and quotes Create.
  const canEditRenewals = useCan('renewals', 'edit');
  const canCreatePolicy = useCan('policies', 'create');
  const canCreateQuote = useCan('quotes', 'create');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState<RenewalStatus | ''>('');

  const { data: item, isLoading, error } = useRenewalQueueItem(id!);
  const updateStatusMutation = useUpdateRenewalQueueItemStatus();

  const handleStatusUpdate = () => {
    if (newStatus && item) {
      updateStatusMutation.mutate(
        // POST goes to the policy id; `routeId` lets the hook refresh the
        // cache this page actually reads, which is keyed on the route id
        // (possibly a legacy Renewal id from an old notification link).
        { policyId: item.policy_id, routeId: id, status: newStatus },
        { onSuccess: () => setShowStatusModal(false) }
      );
    }
  };

  const getExpiryBadgeVariant = (days: number) => {
    if (days < 0) return 'error';
    if (days <= 7) return 'error';
    if (days <= 15) return 'warning';
    if (days <= 30) return 'info';
    return 'default';
  };

  if (isLoading) {
    return <ContentLoader />;
  }

  // A failed request and "this renewal does not exist" are different facts.
  // The queue answers an unknown or out-of-scope id with a 404 (never a 200
  // with no body), so "not found" is decided by the 404 -- any other failure
  // is an error, and must not be presented as the record not existing.
  const isNotFound =
    (axios.isAxiosError(error) && error.response?.status === 404) || (!error && !item);
  if (error || !item) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">
          {isNotFound ? 'Renewal not found' : "Couldn't load this renewal"}
        </h2>
        <p className="text-gray-500 mt-2">
          {isNotFound
            ? 'The renewal you are looking for does not exist.'
            : 'That is not the same as it not existing — please try again.'}
        </p>
        <Link to="/renewals" className="mt-4 inline-block">
          <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
            Back to Renewals
          </Button>
        </Link>
      </div>
    );
  }

  // B2/B9: the insurer's policy number, computed server-side as
  // `display_policy_number` (falls back to our internal number only when the
  // insurer's is blank). Never the renewal's own reference number.
  // B-05: that fallback is labelled as ours wherever it is shown.
  const displayPolicyNumber = policyNumberLabel(item);

  return (
    <div>
      <div className="mb-4">
        <BackButton to="/renewals" />
      </div>
      <PageHeader
        title={displayPolicyNumber}
        subtitle={`Renewal for ${item.customer_name}`}
        breadcrumbs={[
          { label: 'Renewals', href: '/renewals' },
          { label: displayPolicyNumber },
        ]}
        actions={
          <div className="flex items-center gap-3">
            {/* The queue's `update_status` action refuses a customer's POST at
                the permission layer, same as the old id-keyed action -- hide
                the trigger, not only the modal. */}
            {!isCustomer && (
              <>
                {/* B2: the queue LIST already excludes a policy once it has an
                    explicit successor (`renewal_queue` -> `successor_exists`,
                    apps/policies/lifecycle.py), but this DETAIL page is reachable
                    directly by policy id (`resolve_queue_policy` does not apply
                    that exclusion) -- so a stale link or a bookmark can land here
                    after the renewal has already been recorded. Gate the button
                    on `item.successor` itself rather than queue membership, so it
                    disappears the moment a successor is linked, from wherever the
                    page was reached. */}
                {canCreatePolicy && !item.successor && (
                  <Link to={`/policies/new?renewal=${item.policy_id}`}>
                    <Button variant="outline">Record renewal policy</Button>
                  </Link>
                )}
                {canEditRenewals && (
                  <Button variant="outline" onClick={() => setShowStatusModal(true)}>
                    Update Status
                  </Button>
                )}
              </>
            )}
            {canCreateQuote && (
              <Link to={`/quotes/new?renewal=${item.policy_id}`}>
                <Button>Create Renewal Quote</Button>
              </Link>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Policy Details */}
          <Card>
            <CardHeader title="Policy Information" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500">Policy Number</p>
                <PolicyNumber
                  source={item}
                  render={(text) => (
                    <EntityLink
                      kind="policy"
                      id={item.policy_id}
                      name={text}
                      className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
                    />
                  )}
                />
              </div>
              <div>
                <p className="text-sm text-gray-500">Customer</p>
                <EntityLink kind="contact" id={item.customer} name={item.customer_name} className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Insurer</p>
                {/* U-05: the insurer's logo beside its name; blurb in the hover. */}
                <span className="flex items-center gap-2">
                  <InsurerLogo
                    src={item.insurer_logo_url}
                    name={item.insurer_name}
                    description={item.insurer_description}
                    size="xs"
                  />
                  <EntityLink kind="insurer" id={item.insurer} name={item.insurer_name ?? '—'} className="min-w-0 truncate text-sm font-medium text-primary-600 hover:underline dark:text-primary-400" />
                </span>
                {item.insurer_description && (
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{item.insurer_description}</p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">Product</p>
                <p className="text-sm font-medium text-gray-900">{item.product_name}</p>
              </div>
            </div>
            {/* Renewal link neighbours -- the predecessor this policy renewed,
                and/or the successor that renewed it, when either link exists. */}
            {(item.predecessor || item.successor) && (
              <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-6">
                {item.predecessor && (
                  <PolicyRefLink label="Renewal of" policyRef={item.predecessor} />
                )}
                {item.successor && (
                  <PolicyRefLink label="Renewed by" policyRef={item.successor} />
                )}
              </div>
            )}
            {/* B9 review fix: the vehicle-rule hint. Only offered when there
                is no explicit successor link -- the backend already withholds
                the key once one exists (and omits it entirely for a
                customer), but the explicit check keeps this page correct even
                if a stale cached row still carried both. The "Record renewal
                policy" action above uses the same `item.successor` gate (B2),
                so once a successor exists, both disappear together; this hint
                is a pointer to a likely match, not a replacement for that action
                while it is still offered. */}
            {!item.successor && item.possible_successor && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <Link
                  to={`/policies/${item.possible_successor.id}`}
                  className="text-sm text-amber-700 hover:underline dark:text-amber-400"
                >
                  Possible renewal: {policyNumberLabel(item.possible_successor)}
                </Link>
              </div>
            )}
          </Card>

          {/* Renewal Details */}
          <Card>
            <CardHeader title="Renewal Details" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <CalendarIcon className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-500">Expiry Date</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">{formatDate(item.expiry_date)}</p>
                <Badge variant={getExpiryBadgeVariant(item.days_to_expiry)} size="sm" className="mt-2">
                  {item.days_to_expiry < 0
                    ? `${Math.abs(item.days_to_expiry)} days overdue`
                    : `${item.days_to_expiry} days remaining`}
                </Badge>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <CurrencyRupeeIcon className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-600">Current Premium</span>
                </div>
                <p className="text-lg font-semibold text-green-700">
                  {formatCurrency(item.current_premium)}
                </p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <UserIcon className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-blue-600">Assigned To</span>
                </div>
                <p className="text-lg font-semibold text-blue-700">
                  {item.workflow?.assigned_to_name || item.assigned_to_name || 'Unassigned'}
                </p>
              </div>
            </div>
          </Card>

          {/* #345: the insurer's renewal payment link (desk only). */}
          {!isCustomer && <RenewalPaymentLinkCard item={item} routeId={id} />}

          {/* Follow-up Information */}
          <Card>
            <CardHeader title="Follow-up Information" />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Last Contact</span>
                <span className="text-sm font-medium text-gray-900">
                  {item.workflow?.last_contacted_at ? formatDate(item.workflow.last_contacted_at) : 'Never'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Next Follow-up</span>
                <span className="text-sm font-medium text-gray-900">
                  {item.workflow?.next_follow_up ? formatDate(item.workflow.next_follow_up) : 'Not scheduled'}
                </span>
              </div>
              {item.workflow_detail?.notes && (
                <div className="pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-500 mb-2">Notes</p>
                  <p className="text-sm text-gray-900">{item.workflow_detail.notes}</p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card>
            <CardHeader title="Status" />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Current Status</span>
                <Badge variant={item.workflow_status ? getStatusBadgeVariant(item.workflow_status) : 'default'}>
                  {renewalWorkflowStatusLabel(item.workflow_status)}
                </Badge>
              </div>
            </div>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader title="Quick Actions" />
            <div className="space-y-3">
              <Link to={`/policies/${item.policy_id}`} className="block">
                <Button variant="outline" className="w-full justify-start">
                  View Policy
                </Button>
              </Link>
              {item.customer && (
                <Link to={`/customers/${item.customer}`} className="block">
                  <Button variant="outline" className="w-full justify-start">
                    View Customer
                  </Button>
                </Link>
              )}
              {item.successor && (
                <Link to={`/policies/${item.successor.id}`} className="block">
                  <Button variant="outline" className="w-full justify-start">
                    View Renewed Policy
                  </Button>
                </Link>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Update Status Modal. Gated again here (not only on the trigger
          button above): `showStatusModal` is component state, so nothing
          stops it being opened another way, and a customer must never see
          the modal render even momentarily. */}
      <Modal
        isOpen={!isCustomer && canEditRenewals && showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title="Update Renewal Status"
      >
        <div className="space-y-4">
          <Select
            label="New Status"
            options={statusOptions}
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value as RenewalStatus | '')}
          />
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleStatusUpdate}
            isLoading={updateStatusMutation.isPending}
            disabled={!newStatus}
          >
            Update Status
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

function PolicyRefLink({ label, policyRef }: { label: string; policyRef: RenewalQueuePolicyRef }) {
  return (
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <PolicyNumber
        source={policyRef}
        render={(text) => (
          <EntityLink
            kind="policy"
            id={policyRef.id}
            name={text}
            className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
          />
        )}
      />
    </div>
  );
}
